import { useCallback, useContext, useRef } from 'react';

import { TvContext } from '@smart-tv/contexts/tv-context';
import { useStream } from '@smart-tv/components/player/_hooks/use-stream';
import { SentryClient, SpinnerDebugContext } from '@smart-tv/sentry/SentryClient';
import { LiveTvChannel } from '@smart-tv/typings/content';

const SPINNER_TIMEOUT_MS = 10000;

type SpinnerType = 'initial_load' | 'buffering';

interface UseSpinnerTimeoutParams {
  channel?: LiveTvChannel;
  mediaId?: number;
  eventId?: number;
  contentId?: number;
}

const useSpinnerTimeout = (params: UseSpinnerTimeoutParams) => {
  const { channel, mediaId, eventId, contentId } = params;

  const spinnerTimeoutRef = useRef<number | null>(null);
  const sentryEventsSentRef = useRef<{ initial_load: boolean; buffering: boolean }>({
    initial_load: false,
    buffering: false
  });
  const numberOfBufferingEventsSent = useRef(0);

  const { model, platformVersion, connectionType, appVersion, deviceUuid } = useContext(TvContext);
  const { streamType, streamTitles, error } = useStream();

  const gatherDebugContext = useCallback(
    (type: SpinnerType): SpinnerDebugContext => {
      return {
        spinnerType: type,
        durationMs: SPINNER_TIMEOUT_MS,
        streamType: streamType,
        channelSid: channel?.sid,
        eventId: eventId ?? channel?.currentProgram?.eventId,
        mediaId: mediaId,
        contentId: contentId,
        channelName: channel?.name,
        contentTitle: streamTitles?.title,
        tvModel: model,
        tizenVersion: platformVersion,
        connectionType: connectionType,
        appVersion: appVersion,
        deviceUuid: deviceUuid,
        hasStreamError: Boolean(error),
        errorMessage: error?.info,
        bufferingEventsSent: numberOfBufferingEventsSent.current
      };
    },
    [
      streamType,
      channel,
      mediaId,
      eventId,
      contentId,
      streamTitles,
      model,
      platformVersion,
      connectionType,
      appVersion,
      deviceUuid,
      error
    ]
  );

  const startSpinnerTimeout = useCallback(
    (type: SpinnerType) => {
      if (sentryEventsSentRef.current[type] && numberOfBufferingEventsSent.current > 2) {
        return;
      }

      if (spinnerTimeoutRef.current) {
        clearTimeout(spinnerTimeoutRef.current);
      }

      spinnerTimeoutRef.current = window.setTimeout(() => {
        if (type === 'buffering') {
          numberOfBufferingEventsSent.current += 1;
        }
        const context = gatherDebugContext(type);
        SentryClient.trackLongLoadingSpinner(context);
        sentryEventsSentRef.current[type] = true;
      }, SPINNER_TIMEOUT_MS);
    },
    [gatherDebugContext]
  );

  const clearSpinnerTimeout = useCallback(() => {
    if (spinnerTimeoutRef.current) {
      clearTimeout(spinnerTimeoutRef.current);
      spinnerTimeoutRef.current = null;
    }
  }, []);

  return {
    startSpinnerTimeout,
    clearSpinnerTimeout
  };
};

export default useSpinnerTimeout;
